import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowRight, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

import onboardingExplore from "@/assets/onboarding-explore.png";
import onboardingPlan from "@/assets/onboarding-plan.png";
import onboardingShare from "@/assets/onboarding-share.png";

interface OnboardingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const OnboardingDialog = ({ open, onOpenChange }: OnboardingDialogProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      image: onboardingExplore,
      title: t("onboarding.step1Title", "Explorez les voyages"),
      description: t("onboarding.step1Description", "Découvrez les itinéraires créés par notre communauté de voyageurs. Inspirez-vous de leurs aventures pour planifier la vôtre."),
    },
    {
      image: onboardingPlan,
      title: t("onboarding.step2Title", "Préparez votre voyage"),
      description: t("onboarding.step2Description", "Créez votre itinéraire étape par étape. Ajoutez hébergements, restaurants et activités. Gérez votre budget et vos tâches."),
    },
    {
      image: onboardingShare,
      title: t("onboarding.step3Title", "Partagez vos aventures"),
      description: t("onboarding.step3Description", "Documentez votre voyage avec des photos et des notes. Partagez facilement votre itinéraire avec vos proches."),
    },
  ];

  // Save onboarding status to database
  const saveOnboardingStatus = async (isCompleted: boolean, stoppedAtStep?: number) => {
    if (!user) return;

    const { data: existing } = await supabase
      .from("onboarding_status")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (existing) {
      await supabase
        .from("onboarding_status")
        .update({
          is_completed: isCompleted,
          stopped_at_step: isCompleted ? null : stoppedAtStep,
          completed_at: isCompleted ? new Date().toISOString() : null,
        })
        .eq("user_id", user.id);
    } else {
      await supabase
        .from("onboarding_status")
        .insert({
          user_id: user.id,
          is_completed: isCompleted,
          stopped_at_step: isCompleted ? null : stoppedAtStep,
          completed_at: isCompleted ? new Date().toISOString() : null,
        });
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    await saveOnboardingStatus(true);
    onOpenChange(false);
    setCurrentStep(0);
  };

  const handleSkip = async () => {
    await saveOnboardingStatus(false, currentStep);
    onOpenChange(false);
    setCurrentStep(0);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[480px] p-0 overflow-hidden" hideCloseButton>
        <div className="p-6 pb-0">
          <Progress value={progress} className="h-1" />
        </div>
        
        <div className="p-6 pt-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
              className="flex flex-col items-center text-center"
            >
              <motion.div 
                className="w-40 h-40 mb-6 flex items-center justify-center"
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ 
                  duration: 0.4, 
                  delay: 0.1,
                  type: "spring",
                  stiffness: 200,
                  damping: 15
                }}
              >
                <img 
                  src={steps[currentStep].image} 
                  alt={steps[currentStep].title}
                  className="w-full h-full object-contain"
                />
              </motion.div>
              
              <motion.h2 
                className="text-xl font-semibold mb-3"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                {steps[currentStep].title}
              </motion.h2>
              
              <motion.p 
                className="text-muted-foreground leading-relaxed"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                {steps[currentStep].description}
              </motion.p>
            </motion.div>
          </AnimatePresence>
          
          {/* Step indicators */}
          <div className="flex justify-center gap-2 mt-8 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? "bg-primary w-6" 
                    : index < currentStep 
                      ? "bg-primary/50" 
                      : "bg-muted"
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="flex-1"
            >
              {t("onboarding.skip", "Passer")}
            </Button>
            <Button
              onClick={handleNext}
              className="flex-1 gap-2"
            >
              {isLastStep ? (
                <>
                  {t("onboarding.start", "Commencer")}
                  <Check className="w-4 h-4" />
                </>
              ) : (
                <>
                  {t("onboarding.next", "Suivant")}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Check if user has completed onboarding from database
export const checkOnboardingStatus = async (userId: string): Promise<{ isCompleted: boolean; stoppedAtStep?: number }> => {
  const { data } = await supabase
    .from("onboarding_status")
    .select("is_completed, stopped_at_step")
    .eq("user_id", userId)
    .single();

  if (!data) {
    return { isCompleted: false };
  }

  return {
    isCompleted: data.is_completed,
    stoppedAtStep: data.stopped_at_step ?? undefined,
  };
};

export default OnboardingDialog;
